/**
 * LooksGoodCardDe — German variant of LooksGoodCard.
 *
 * Heyflow widget tag:
 *   <div data-conny-widget="looks-good-card-de"></div>
 *
 * Reads the same sessionStorage keys as LooksGoodCard.
 */
import { useQuery } from "@tanstack/react-query";
import { getFunnelContext, getLandlord, Landlord } from "../../shared/api";
import { readAddress } from "../../shared/address";
import { AnimatedNumber } from "../../shared/AnimatedNumber";
import {
  CheckIcon,
  CircleCheckIcon,
  FileIcon,
  ShieldIcon,
} from "../../shared/icons";
import { STORAGE_KEYS, useStorage } from "../../shared/storage";

interface Resolved {
  landlord: Landlord | null;
  type: string;
}

function useResolvedLandlord(): Resolved | null {
  const [knownKey] = useStorage(STORAGE_KEYS.landlordKnownKey);
  const [landlordType] = useStorage(STORAGE_KEYS.landlordType);
  const [street] = useStorage(STORAGE_KEYS.street);
  const [house] = useStorage(STORAGE_KEYS.house);
  const [zip] = useStorage(STORAGE_KEYS.zip);

  const explicit = useQuery({
    queryKey: ["landlord", knownKey],
    queryFn: () => getLandlord(knownKey),
    enabled: Boolean(knownKey && knownKey !== "other_commercial"),
    staleTime: 30 * 60_000,
  });

  const addr = readAddress();
  const fromAddr = street && house && zip ? addr : null;
  const ctx = useQuery({
    queryKey: [
      "funnel-context",
      fromAddr?.street,
      fromAddr?.houseNumber,
      fromAddr?.zip,
    ],
    queryFn: () =>
      getFunnelContext({
        street: fromAddr!.street,
        house: fromAddr!.houseNumber,
        zip: fromAddr!.zip,
      }),
    enabled: Boolean(fromAddr),
    staleTime: 5 * 60_000,
  });

  if (explicit.data)
    return { landlord: explicit.data, type: "known_corporate" };
  if (ctx.data?.landlord)
    return {
      landlord: ctx.data.landlord,
      type: ctx.data.landlord.type || "known_corporate",
    };
  if (landlordType === "private" || landlordType === "other_corporate") {
    return { landlord: null, type: landlordType };
  }
  return null;
}

export function LooksGoodCardDe() {
  const resolved = useResolvedLandlord();

  return (
    <div className="cn">
      {resolved && <LandlordReassurance resolved={resolved} />}

      <details className="cn-card">
        <summary style={{ listStyle: "none", cursor: "pointer" }}>
          <div className="cn-top">
            <div className="cn-icon">
              <CircleCheckIcon />
            </div>
            <div
              className="cn-head"
              style={{ display: "flex", alignItems: "center", gap: 10 }}
            >
              <span style={{ flex: 1 }}>
                Mietvertrag eignet sich wahrscheinlich
              </span>
              <span className="cn-chip">Wahrscheinlich</span>
            </div>
          </div>
        </summary>
        <div className="cn-body">
          <Bullet>Einzugsdatum nach dem 1.&nbsp;Juni&nbsp;2015</Bullet>
          <Bullet>
            Mietpreisbremse gilt für möblierte und unmöblierte Wohnungen
          </Bullet>
        </div>
      </details>

      <details className="cn-card">
        <summary style={{ listStyle: "none", cursor: "pointer" }}>
          <div className="cn-top">
            <div className="cn-icon">
              <FileIcon />
            </div>
            <div
              className="cn-head"
              style={{ display: "flex", alignItems: "center", gap: 10 }}
            >
              <span style={{ flex: 1 }}>
                Genug Daten für eine erste Einschätzung
              </span>
              <span className="cn-chip">Bereit</span>
            </div>
          </div>
        </summary>
        <div className="cn-body">
          <CheckLine>
            Wir können jetzt Ihr wahrscheinliches Einsparpotenzial auf Basis
            Ihrer Miete berechnen.
          </CheckLine>
        </div>
      </details>
    </div>
  );
}

function LandlordReassurance({ resolved }: { resolved: Resolved }) {
  const { landlord, type } = resolved;
  let name: string;
  let chip: React.ReactNode;
  let point1: React.ReactNode;
  let point2: string;
  let point3: string;

  if (landlord && landlord.count) {
    name = landlord.name;
    chip = (
      <>
        <AnimatedNumber value={landlord.count} />
        &nbsp;Fälle
      </>
    );
    point1 = <>Fälle mit {landlord.name} bearbeitet</>;
    point2 =
      "Große Vermieter kennen uns. Daher werden Conny-Fälle oft von Spezialisten direkt in der Rechtsabteilung bearbeitet, mit denen Sie nie zu tun haben werden.";
    point3 =
      "Vermieter können Ihr Mietverhältnis natürlich nicht beenden, nur weil Sie Ihr Recht auf eine faire Miete einfordern.";
  } else if (type === "other_corporate") {
    name = "Gewerblicher Vermieter";
    chip = "Häufig";
    point1 =
      "Die allermeisten Wohnungsunternehmen kennen uns bereits aus vielen hundert Fällen.";
    point2 =
      "Conny Fälle landen meist direkt bei Spezialisten in den Rechtsabteilungen, die mit der Mieterbetreuung nichts zu tun haben.";
    point3 =
      "Vermieter können Ihr Mietverhältnis natürlich nicht beenden, nur weil Sie Ihr Recht auf eine faire Miete einfordern.";
  } else if (type === "private") {
    name = "Privater Vermieter";
    chip = "Häufig";
    point1 =
      "In rund zwei Drittel aller Fälle geht Conny gegen private Vermieter vor.";
    point2 =
      "Wir kontaktieren Ihren Vermieter nur, wenn Sie uns mit der Mietsenkung mandatieren. Sie verpflichten sich mit der Prüfung zu nichts.";
    point3 =
      "Als Mieter müssen Sie keine Angst vor Ihrem Vermieter haben. Vermieter können nicht gegen Sie vorgehen, nur weil Sie von Ihrem guten Recht Gebrauch machen.";
  } else {
    return null;
  }

  return (
    <details className="cn-card">
      <summary style={{ listStyle: "none", cursor: "pointer" }}>
        <div className="cn-top">
          <div className="cn-icon">
            <ShieldIcon />
          </div>
          <div
            className="cn-head"
            style={{ display: "flex", alignItems: "center", gap: 10 }}
          >
            <span style={{ flex: 1, overflowWrap: "anywhere" }}>{name}</span>
            <span className="cn-chip">{chip}</span>
          </div>
        </div>
      </summary>
      <div className="cn-body">
        <Bullet>{point1}</Bullet>
        <Bullet>{point2}</Bullet>
        <Bullet>{point3}</Bullet>
      </div>
    </details>
  );
}

function CheckLine({
  children,
  textClassName = "cn-bullet-text",
}: {
  children: React.ReactNode;
  textClassName?: string;
}) {
  return (
    <div className="cn-bullet">
      <span className="cn-row-left">
        <span className="cn-check">
          <CheckIcon />
        </span>
        <span className={textClassName}>{children}</span>
      </span>
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return <CheckLine>{children}</CheckLine>;
}
